import React, {useEffect, useRef} from 'react';
import {
  Pressable,
  Platform,
  Animated,
  GestureResponderEvent,
} from 'react-native';
import {useTheme} from 'styled-components';
import {Theme} from '../../theme/lightTheme';
import {MyPressableProps} from '../../interfaces/components/myPressable';
import styled from 'styled-components/native';

/**
 * Styled component for the container with overflow hidden.
 */
const Container = styled(Animated.View)`
  overflow: hidden;
`;

/**
 * A custom pressable component.
 *
 * @component
 *
 * @param {MyPressableProps} props - The pressable component props.
 * @param {StyleProp<ViewStyle>} [props.style] - The style for the pressable component.
 * @param {object} [props.android_ripple] - The ripple effect configuration for Android.
 * @param {React.ReactNode} [props.children] - The child components or elements.
 * @param {boolean} [props.iosOpacityEffect=false] - Enable iOS opacity effect when pressed.
 * @param {StyleProp<ViewStyle>} [props.containerStyle] - The style for the container wrapping the pressable component.
 * @param {number} [props.borderRadius=0] - The border radius for the container.
 * @param {boolean} [props.foreground=false] - Whether the ripple effect should appear as a foreground or background effect on Android.
 * @param {boolean} [props.secondaryRipple=false] - Whether to use the secondary color for the ripple effect.
 * @param {boolean} [props.primaryRipple=false] - Whether to use the primary color for the ripple effect.
 * @param {boolean} [props.disabled=false] - Whether the pressable is disabled.
 * @param {boolean} [props.disabledNoOpacity=false] - Disable the button without an opacity effect.
 * @param {string} [props.backgroundColor='transparent'] - The background color of the pressable.
 * @param {PressableProps} [restOfProps] - Additional props to be passed to the Pressable component.
 *
 * @returns {React.ReactElement} The rendered pressable component.
 */
const MyPressable: React.FC<MyPressableProps> = ({
  style,
  android_ripple,
  children,
  iosOpacityEffect = false,
  containerStyle,
  borderRadius = 0,
  foreground = false,
  secondaryRipple = false,
  primaryRipple = false,
  disabled = false,
  disabledNoOpacity = false,
  enableScale = false,
  backgroundColor = 'transparent',
  disableOpacityAnimation,
  ...restOfProps
}) => {
  /**
   * Represents the current theme.
   * @type {Theme}
   */
  const theme = useTheme() as Theme;
  /**
   * Ref for animation controller.
   * @type {Animated.Value}
   */
  const animationController = useRef<Animated.Value>(
    new Animated.Value(0),
  ).current;

  /**
   * Opacity value for animation.
   * @type {Animated.AnimatedInterpolation}
   */
  const opacity = animationController.interpolate({
    inputRange: [0, 1],
    outputRange: [disableOpacityAnimation ? 1 : 0.4, 1],
  });

  /**
   * Effect to animate the pressable component.
   */
  useEffect(() => {
    Animated.timing(animationController, {
      duration: 200,
      toValue: disabled ? 0 : 1,
      useNativeDriver: Platform.OS !== 'web',
    }).start();
  }, [animationController, disabled]);

  const scale = useRef(new Animated.Value(1)).current;

  /**
   * Get the ripple color based on primary or secondary ripple settings.
   * @returns {string} The ripple color.
   */
  const getRippleColor = () => {
    if (primaryRipple) {
      return `${theme.ripple}2F`;
    } else if (secondaryRipple) {
      return `${theme.secondary}2F`;
    }
    return `${theme.primary}2F`;
  };

  const pressInAnimation = (event: GestureResponderEvent) => {
    if (enableScale) {
      Animated.spring(scale, {
        toValue: 0.95,
        useNativeDriver: Platform.OS !== 'web',
      }).start();
    }
    restOfProps?.onPressIn && restOfProps?.onPressIn(event);
  };

  const pressOutAnimation = (event: GestureResponderEvent) => {
    if (enableScale) {
      Animated.spring(scale, {
        toValue: 1,
        useNativeDriver: Platform.OS !== 'web',
      }).start();
    }
    restOfProps?.onPressOut && restOfProps?.onPressOut(event);
  };

  return (
    <Container style={[{borderRadius}, containerStyle, {opacity}]}>
      <Pressable
        style={({pressed}) => [
          style,
          {
            backgroundColor:
              Platform.OS !== 'android' && pressed
                ? getRippleColor()
                : backgroundColor,
            opacity: iosOpacityEffect && pressed ? 0.5 : 1,
          },
        ]}
        onPressIn={pressInAnimation}
        onPressOut={pressOutAnimation}
        android_ripple={
          android_ripple || {
            color: getRippleColor(),
            foreground,
          }
        }
        disabled={disabled || disabledNoOpacity}
        {...restOfProps}>
        {children}
      </Pressable>
    </Container>
  );
};

export default MyPressable;
